import { useMemo, type ReactElement } from 'react'
import { useRequiredParams } from '@util'
import { type WithProgramId } from '@terros/common'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQueryDisclosure } from '@hooks'
import { getProgramPermissionIds, useProgram, useTranslation } from '@front/model'
import { NestedNavigation, NotReady, Page, PermissionCheckWrapper, TButton, type PageRoute } from '@components'
import { ProgramTestDriveButton, ProgramTestDriveModal } from './testDrive'
import { ProgramContext } from './context'

function ProgramPage(): ReactElement {
  const { t } = useTranslation()
  const { programId } = useRequiredParams<WithProgramId>()
  const { data: program, isLoading, error, refetch, isFetching } = useProgram(programId)

  const contextValue = useMemo(() => {
    if (!program) return
    return { program, refetch, isFetching }
  }, [program, refetch, isFetching])

  const navigate = useNavigate()
  const [isTestDriveOpen, { open: openTestDrive, close: closeTestDrive }] = useQueryDisclosure('test-drive')

  if (isLoading || error || !program || !contextValue)
    return <NotReady data={program} error={error} id='Program' isLoading={isLoading} />

  const routes: PageRoute[] = [
    {
      label: 'Setup',
      path: `/program/${programId}/setup`,
      icon: 'settings',
    },
    {
      label: 'Tasks',
      path: `/program/${programId}/tasks`,
      icon: 'assignment',
    },
    {
      label: 'Users',
      path: `/program/${programId}/users`,
      icon: 'groups',
    },
    {
      label: 'Summary',
      path: `/program/${programId}/summary`,
      icon: 'table',
    },
  ]

  return (
    <PermissionCheckWrapper permissionType='program:manage' targetId={getProgramPermissionIds(program)}>
      <ProgramContext.Provider value={contextValue}>
        <Page>
          <Page.Heading
            title={program.name}
            className='h-[85px]'
            handleGoBack={() => navigate({ to: '/program' })}
            back
          >
            <ProgramTestDriveButton onClick={openTestDrive} />
            <TButton component={Link} to={`/program/${program.programId}/view`} icon='bar_chart' variant='gray'>
              {t('View Program Stats')}
            </TButton>
          </Page.Heading>
          <NestedNavigation routes={routes} />
          <ProgramTestDriveModal program={program} opened={isTestDriveOpen} onClose={closeTestDrive} />
        </Page>
      </ProgramContext.Provider>
    </PermissionCheckWrapper>
  )
}

export const Route = createFileRoute('/program_/$programId')({
  component: ProgramPage,
})
